/**
 * Comment detail slide-over panel.
 *
 * Shows full comment body, author details, moderation metadata,
 * and status change buttons.
 */

import { Badge, Button } from "@cloudflare/kumo";
import { useLingui } from "@lingui/react/macro";
import { X, Check, Trash, Warning, UserCircle, EnvelopeSimple } from "@phosphor-icons/react";
import * as React from "react";
import type { ComponentProps } from "react";

import type { AdminComment, CommentStatus } from "../../lib/api/comments.js";

export interface CommentDetailProps {
	comment: AdminComment;
	onClose: () => void;
	onStatusChange: (id: string, status: CommentStatus) => void;
	onDelete: (id: string) => void;
	isAdmin: boolean;
	isStatusPending: boolean;
}

export function CommentDetail({
	comment,
	onClose,
	onStatusChange,
	onDelete,
	isAdmin,
	isStatusPending,
}: CommentDetailProps) {
	const { t } = useLingui();

	// Close on Escape
	React.useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape" && !e.defaultPrevented) {
				e.preventDefault();
				onClose();
			}
		};
		document.addEventListener("keydown", handler);
		return () => document.removeEventListener("keydown", handler);
	}, [onClose]);

	const date = new Date(comment.createdAt);

	return (
		<>
			{/* Backdrop */}
			<div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} aria-hidden="true" />

			{/* Panel */}
			<div className="fixed inset-y-0 end-0 z-50 w-full max-w-lg overflow-y-auto bg-kumo-base border-s shadow-lg">
				{/* Header */}
				<div className="flex items-center justify-between border-b px-6 py-4">
					<h2 className="text-lg font-semibold">{t`Comment Detail`}</h2>
					<Button variant="ghost" shape="square" onClick={onClose} aria-label={t`Close`}>
						<X className="h-5 w-5" />
					</Button>
				</div>

				{/* Content */}
				<div className="space-y-6 p-6">
					{/* Status */}
					<div className="flex items-center justify-between">
						<CommentStatusBadge status={comment.status} />
						<span className="text-sm text-kumo-subtle">
							{date.toLocaleDateString()} {date.toLocaleTimeString()}
						</span>
					</div>

					{/* Author info */}
					<div className="rounded-lg border bg-kumo-base p-4 space-y-3">
						<h3 className="text-sm font-semibold text-kumo-subtle uppercase tracking-wider">
							{t`Author`}
						</h3>
						<div className="space-y-2">
							<div className="flex items-center gap-2">
								<UserCircle className="h-4 w-4 text-kumo-subtle" />
								<span className="font-medium">{comment.authorName}</span>
								{comment.authorUserId && <Badge variant="secondary">{t`Registered user`}</Badge>}
							</div>
							<div className="flex items-center gap-2">
								<EnvelopeSimple className="h-4 w-4 text-kumo-subtle" />
								<span className="text-sm text-kumo-subtle">{comment.authorEmail}</span>
							</div>
						</div>
					</div>

					{/* Comment body */}
					<div className="rounded-lg border bg-kumo-base p-4 space-y-3">
						<h3 className="text-sm font-semibold text-kumo-subtle uppercase tracking-wider">
							{t`Comment`}
						</h3>
						<p className="text-sm whitespace-pre-wrap break-words">{comment.body}</p>
					</div>

					{/* Content reference */}
					<div className="rounded-lg border bg-kumo-base p-4 space-y-2">
						<h3 className="text-sm font-semibold text-kumo-subtle uppercase tracking-wider">
							{t`Content`}
						</h3>
						<p className="text-sm">
							<span className="text-kumo-subtle">{t`Collection:`}</span>{" "}
							<span className="font-medium">{comment.collection}</span>
						</p>
						<p className="text-sm">
							<span className="text-kumo-subtle">{t`Content ID:`}</span>{" "}
							<code className="bg-kumo-tint px-1.5 py-0.5 rounded text-xs">
								{comment.contentId}
							</code>
						</p>
						{comment.parentId && (
							<p className="text-sm">
								<span className="text-kumo-subtle">{t`Reply to:`}</span>{" "}
								<code className="bg-kumo-tint px-1.5 py-0.5 rounded text-xs">
									{comment.parentId}
								</code>
							</p>
						)}
					</div>

					{/* Moderation metadata */}
					{comment.moderationMetadata && Object.keys(comment.moderationMetadata).length > 0 && (
						<div className="rounded-lg border bg-kumo-base p-4 space-y-3">
							<h3 className="text-sm font-semibold text-kumo-subtle uppercase tracking-wider">
								{t`Moderation Signals`}
							</h3>
							<pre className="text-xs bg-kumo-tint rounded p-3 overflow-x-auto">
								{JSON.stringify(comment.moderationMetadata, null, 2)}
							</pre>
						</div>
					)}
				</div>

				{/* Footer actions */}
				<div className="border-t px-6 py-4 space-y-3">
					<div className="flex gap-2">
						{comment.status !== "approved" && (
							<Button
								icon={<Check />}
								onClick={() => onStatusChange(comment.id, "approved")}
								disabled={isStatusPending}
								className="flex-1"
							>
								{t`Approve`}
							</Button>
						)}
						{comment.status !== "spam" && (
							<Button
								variant="outline"
								icon={<Warning />}
								onClick={() => onStatusChange(comment.id, "spam")}
								disabled={isStatusPending}
								className="flex-1"
							>
								{t`Spam`}
							</Button>
						)}
						{comment.status !== "trash" && (
							<Button
								variant="outline"
								icon={<Trash />}
								onClick={() => onStatusChange(comment.id, "trash")}
								disabled={isStatusPending}
								className="flex-1"
							>
								{t`Trash`}
							</Button>
						)}
					</div>
					{isAdmin && (
						<Button
							variant="destructive"
							icon={<Trash />}
							onClick={() => onDelete(comment.id)}
							disabled={isStatusPending}
							className="w-full"
						>
							{t`Delete Permanently`}
						</Button>
					)}
				</div>
			</div>
		</>
	);
}

export function CommentStatusBadge({ status }: { status: CommentStatus }) {
	const { t } = useLingui();

	const labels: Record<CommentStatus, string> = {
		approved: t`approved`,
		pending: t`pending`,
		spam: t`spam`,
		trash: t`trash`,
	};

	const variants: Record<CommentStatus, ComponentProps<typeof Badge>["variant"]> = {
		approved: "success",
		pending: "warning",
		spam: "error",
		trash: "neutral",
	};

	return <Badge variant={variants[status]}>{labels[status]}</Badge>;
}
